import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface TrustedDevice {
  id: string;
  device_id: string;
  device_name: string;
  platform: string;
  is_trusted: boolean;
  last_seen: string;
  created_at: string;
}

export const useTrustedDevice = (user: User | null) => {
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [isCurrentDeviceTrusted, setIsCurrentDeviceTrusted] = useState(false);
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);

  // Generate unique device fingerprint
  const generateDeviceId = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
      navigator.hardwareConcurrency || 0,
      (navigator as any).deviceMemory || 0
    ].join('|');
    
    // Create hash of fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36);
  };

  // Get or create device ID
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('trusted_device_id');
    if (!deviceId) {
      deviceId = generateDeviceId();
      localStorage.setItem('trusted_device_id', deviceId);
    }
    return deviceId;
  };

  // Register current device as trusted
  const registerDeviceAsTrusted = async (deviceName?: string) => {
    if (!user || !currentDeviceId) return false;

    try {
      const { data, error } = await supabase
        .from('user_devices')
        .upsert({
          user_id: user.id,
          device_id: currentDeviceId,
          device_name: deviceName || `${navigator.platform} - ${new Date().toLocaleDateString('th-TH')}`,
          user_agent: navigator.userAgent,
          platform: navigator.platform,
          is_trusted: true,
          last_seen: new Date().toISOString()
        }, {
          onConflict: 'user_id,device_id'
        });

      if (error) {
        console.error('Error registering trusted device:', error);
        return false;
      }

      setIsCurrentDeviceTrusted(true);
      await loadTrustedDevices();
      return true;
    } catch (error) {
      console.error('Exception registering trusted device:', error);
      return false;
    }
  };

  // Check if current device is trusted
  const checkDeviceTrust = async () => {
    if (!user || !currentDeviceId) return false;

    try {
      const { data, error } = await supabase
        .from('user_devices')
        .select('is_trusted, revoked_at')
        .eq('user_id', user.id)
        .eq('device_id', currentDeviceId)
        .maybeSingle();

      if (error) {
        console.error('Error checking device trust:', error);
        return false;
      }

      const isTrusted = data?.is_trusted && !data?.revoked_at;
      setIsCurrentDeviceTrusted(isTrusted);
      
      // Update last_seen if trusted
      if (isTrusted) {
        await supabase
          .from('user_devices')
          .update({ last_seen: new Date().toISOString() })
          .eq('user_id', user.id)
          .eq('device_id', currentDeviceId);
      }
      
      return isTrusted;
    } catch (error) {
      console.error('Exception checking device trust:', error);
      return false;
    }
  };

  // Load all trusted devices for user
  const loadTrustedDevices = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_devices')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_trusted', true)
        .is('revoked_at', null)
        .order('last_seen', { ascending: false });

      if (error) {
        console.error('Error loading trusted devices:', error);
        return;
      }

      setTrustedDevices(data || []);
    } catch (error) {
      console.error('Exception loading trusted devices:', error);
    }
  };

  // Revoke device trust
  const revokeDeviceTrust = async (deviceId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_devices')
        .update({ 
          is_trusted: false,
          revoked_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('device_id', deviceId);

      if (error) {
        console.error('Error revoking device trust:', error);
        return false;
      }

      // If revoking current device, update state
      if (deviceId === currentDeviceId) {
        setIsCurrentDeviceTrusted(false);
      }

      await loadTrustedDevices();
      return true;
    } catch (error) {
      console.error('Exception revoking device trust:', error);
      return false;
    }
  };

  // Initialize device ID and check trust status
  useEffect(() => {
    const deviceId = getDeviceId();
    setCurrentDeviceId(deviceId);
  }, []);

  useEffect(() => {
    if (user && currentDeviceId) {
      const initializeDevice = async () => {
        setLoading(true);
        await checkDeviceTrust();
        await loadTrustedDevices();
        setLoading(false);
      };
      initializeDevice();
    } else {
      setLoading(false);
    }
  }, [user, currentDeviceId]);

  return {
    currentDeviceId,
    isCurrentDeviceTrusted,
    trustedDevices,
    loading,
    registerDeviceAsTrusted,
    checkDeviceTrust,
    revokeDeviceTrust,
    loadTrustedDevices
  };
};